import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Free-text field with a suggestion list under it — the shared mechanics behind
 * FacilityTypeahead (Origin / Destination Name) and CarrierTypeahead (Carrier Name) on the gate
 * check-in form.
 *
 * Deliberately NOT a <select>: the guard must still be able to type a facility or carrier we have
 * never seen — walk-up freight is the case where the record does not exist yet, so constraining
 * these fields to known rows would block the very scenario the form is for. Suggestions are an
 * accelerator over free text, not a replacement for it, which is also why `onPick` is separate
 * from `onChange`: typing reports only the text, picking reports the whole record so the caller
 * can fill the fields beside it in one go.
 *
 * Everything specific to *what* is being searched lives in props — `search`, `getLabel`,
 * `getSecondary`, `getKey` — so the dropdown behaviour (debounce, out-of-order response handling,
 * keyboard nav, click-outside) exists once rather than once per searchable field.
 *
 * Styling mirrors DisplayDetail.jsx's editable container so these fields sit flush with the plain
 * fields around them.
 */
export function RecordTypeahead({
  label,
  value,
  onChange,
  onPick,
  search,
  getLabel,
  getSecondary,
  getKey,
  /** Set false to hold the search off entirely (e.g. no office code resolved yet). */
  enabled = true,
  className = "",
  disabled = false,
  minChars = 2,
  placeholder,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  /** The bordered input box — the rect the suggestion list is anchored to. */
  const fieldRef = useRef(null);
  /** The portalled list; needed by the click-outside check since it is not inside containerRef. */
  const listRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const listboxId = "record-typeahead-" + useId();
  /** The text last written by a pick — used to suppress the search that pick would re-trigger. */
  const pickedTextRef = useRef(null);
  /** Held in a ref so an inline arrow passed as `search` cannot restart the debounce each render. */
  const searchRef = useRef(search);
  searchRef.current = search;

  const term = String(value ?? "");

  // Debounced search. The 250ms is what keeps this to roughly one request per pause in typing
  // rather than one per keystroke; the cancelled flag drops responses that land out of order,
  // which is the difference between a stale list and a list that matches what is in the box.
  useEffect(() => {
    const q = term.trim();

    if (pickedTextRef.current !== null && pickedTextRef.current === term) {
      // The user just chose this exact entry — re-searching it would reopen the list on top of
      // the selection they already made.
      return undefined;
    }
    if (disabled || !enabled || q.length < minChars) {
      setSuggestions([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchRef.current(q);
        if (cancelled) return;
        const rows = Array.isArray(results) ? results : [];
        setSuggestions(rows);
        setActiveIndex(-1);
        if (rows.length > 0) setOpen(true);
      } catch {
        // Non-fatal by design: the field stays usable as plain free text, so a failing or
        // forbidden lookup can never block a check-in.
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [term, enabled, disabled, minChars]);

  // Close when focus or a click leaves the field entirely. Listening on the document rather than
  // the input's onBlur is what lets a suggestion be clicked at all — onBlur fires before the
  // click lands and would tear the list down from under the pointer.
  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      // The list is portalled to <body>, so it is NOT inside containerRef — check it separately
      // or every click on a suggestion would read as "focus left the field" and close the list.
      if (containerRef.current?.contains(e.target)) return;
      if (listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("focusin", handle);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("focusin", handle);
    };
  }, [open]);

  const choose = useCallback(
    (record) => {
      const text = String(getLabel(record) ?? "").trim();
      pickedTextRef.current = text;
      onChange?.(text);
      onPick?.(record);
      setOpen(false);
      setActiveIndex(-1);
    },
    [onChange, onPick, getLabel]
  );

  // Anchor the portalled list to the field's viewport rect, re-measuring on scroll and resize.
  // `true` on the scroll listener catches scrolling inside the modal body, not just the window.
  useLayoutEffect(() => {
    if (!open || suggestions.length === 0) {
      setMenuPos(null);
      return undefined;
    }
    const place = () => {
      const rect = fieldRef.current?.getBoundingClientRect();
      if (!rect) return;
      const GAP = 4;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Drop upward only when below is genuinely cramped and above has more room, so the list
      // does not jump around for the usual case of a field near the middle of the form.
      const flip = spaceBelow < 160 && spaceAbove > spaceBelow;
      setMenuPos({
        left: rect.left,
        width: rect.width,
        top: flip ? undefined : rect.bottom + GAP,
        bottom: flip ? window.innerHeight - rect.top + GAP : undefined,
        maxHeight: Math.max(120, (flip ? spaceAbove : spaceBelow) - GAP * 3),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, suggestions.length]);

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length > 0) {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        choose(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative flex flex-col min-w-[clamp(8rem,12vw,10rem)] ${className}`}>
      <span className="text-dense-xs text-gray-500 dark:text-acs-dark-subtext">{label}</span>
      <div
        ref={fieldRef}
        className="relative flex items-center justify-between w-full
                   px-fluid-2 py-fluid-1.5 mt-fluid-0.25
                   bg-[#FAFAFB] dark:bg-acs-dark-surface text-gray-700 dark:text-acs-dark-text
                   rounded-md border border-gray-300 dark:border-acs-dark-border
                   text-fluid-sm font-inter
                   focus-within:ring-2 focus-within:ring-blue-400 dark:focus-within:ring-[#223688]
                   focus-within:border-blue-400 dark:focus-within:border-[#223688]
                   transition-none ease-in-out"
      >
        <input
          type="text"
          value={term}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          onChange={(e) => {
            pickedTextRef.current = null;
            onChange?.(e.target.value);
          }}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onKeyDown={onKeyDown}
          className="w-full bg-transparent outline-none focus:outline-none dark:text-acs-dark-text"
        />
        {loading && (
          <span className="ml-fluid-0.75 flex-shrink-0 text-fluid-xs text-gray-400 dark:text-acs-dark-subtext">
            …
          </span>
        )}
      </div>

      {/* Portalled to <body> on purpose: the check-in form's section cards carry overflow-hidden
          (sectionCardClass, for the rounded header bar), which clipped an in-flow absolute list at
          the card's bottom edge — the Carrier suggestions were cut off by the Equipment header. */}
      {open && suggestions.length > 0 && menuPos && createPortal(
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          style={{
            position: "fixed",
            left: menuPos.left,
            width: menuPos.width,
            top: menuPos.top,
            bottom: menuPos.bottom,
            maxHeight: menuPos.maxHeight,
            zIndex: 1000,
          }}
          className="overflow-auto
                     rounded-md border border-gray-300 dark:border-acs-dark-border
                     bg-white dark:bg-acs-dark-surface shadow-lg"
        >
          {suggestions.map((record, i) => (
            <li key={getKey(record, i)} role="option" aria-selected={i === activeIndex}>
              <button
                type="button"
                // mousedown, not click: the input's focus loss would otherwise close the list
                // before the click completes and the pick would be swallowed.
                onMouseDown={(e) => { e.preventDefault(); choose(record); }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full text-left px-fluid-2 py-fluid-1 text-fluid-sm font-inter
                            ${i === activeIndex ? "bg-blue-50 dark:bg-blue-900/20" : "bg-transparent"}
                            text-gray-700 dark:text-acs-dark-text`}
              >
                <span className="block truncate">{String(getLabel(record) ?? "").trim()}</span>
                {getSecondary?.(record) && (
                  <span className="block truncate text-fluid-xs text-gray-500 dark:text-acs-dark-subtext">
                    {getSecondary(record)}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default RecordTypeahead;
